"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "No subscription gate",
    rule: "Creating a Price List isn't limited by your plan",
  },
  {
    topic: "Unique name",
    rule: "Price List names must be unique per company, and unlike every other All Master module this check is case-sensitive",
  },
  {
    topic: "Currency is required in practice",
    rule: "The form and model both technically allow it to be left blank, but saving fails without a valid company currency. The form defaults it to your base currency so this rarely comes up",
  },
  {
    topic: "No currency cross-check",
    rule: "Nothing stops a price list built in one currency from pre-filling its prices onto a document in a different currency",
  },
  {
    topic: "Gross Price is decorative",
    rule: "It's captured and displayed but never read by any pricing or posting logic",
  },
  {
    topic: "No active/inactive status",
    rule: "There's no deactivate action on a Price List, only Edit and Delete",
  },
  {
    topic: "Deleting",
    rule: "Blocked if the price list is assigned to any Customer, or used on any Quote, Sales Order, Delivery Challan, Invoice, or Recurring Invoice",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. A Price List is a set of Net and Gross prices per item" },
      { heading: "2. It pre-fills prices, it doesn't enforce them" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function PriceListPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Price List
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone setting up custom item pricing for specific customers
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <span className="geist-medium text-[#111827]">Price List</span> is
            a named set of per-item prices in one currency that you can assign
            to a customer. The part worth understanding clearly is what it
            actually does when you put it to use: it pre-fills a rate, it
            doesn&apos;t lock one in.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Structure */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. A Price List is a set of Net and Gross prices per item
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Each list has its own header details and a table of item-level prices.
          </p>
          <Definitions
            items={[
              {
                term: "Price List Name and Remarks",
                desc: "Name must be unique across your company. Remarks is required.",
              },
              {
                term: "Currency",
                desc: "Every price on the list is in this one currency. It defaults to your company's base currency from Settings → Currencies, and the picker is that same currency list.",
              },
              {
                term: "Each item line",
                desc: "Picks an Item Group, then an Item within it, and carries both a Net Price and a Gross Price.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Price List form with an item row showing Net Price and Gross Price." />

          {/* 2. Pre-fill not enforcement */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. It pre-fills prices, it doesn&apos;t enforce them
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            This is the single most important thing to understand before you
            rely on a Price List.
          </p>
          <Definitions
            items={[
              {
                term: "Assigning a Price List to a customer",
                desc: "Makes its Net Price the starting rate whenever that customer's document lines pick a matching item.",
              },
              {
                term: "That rate is fully editable",
                desc: "Anyone filling in the document can overtype it, and nothing checks the final saved price against the list afterward.",
              },
              {
                term: "Gross Price",
                desc: "Is stored and shown on the list itself, but it never appears anywhere else and never affects a document.",
              },
              {
                term: "Restrict to price list items",
                desc: "A separate customer setting that does something real: turn it on, and that customer's item pickers only show items that actually appear on their assigned price list.",
              },
            ]}
          />
          <Callout type="note">
            There&apos;s also no check that the price list&apos;s currency
            matches the document&apos;s currency. A price list built in one
            currency will still pre-fill its numbers onto a document in a
            different one without converting anything.
          </Callout>
          <Screenshot caption="Screenshot: an invoice line auto-filled from a customer's assigned Price List, still editable." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <Link
            href="/documentation/books/all-master/item-group"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Item Group <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              How each price list line's items are grouped
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/customer-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Customers <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where a Price List actually gets assigned
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where a pre-filled price can still be changed
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
